import { readFile, writeFile } from "node:fs/promises";
import { join } from "node:path";
import { debugLog } from "../../core/debug";
import { listTestFiles, TEST_INDEX_FILE, TESTS_DIR } from "../../core/test-files";
import type { CoverageState } from "./graph";
import { CRITICALITY_LEVELS } from "./validation";

const STEP_VERBS = /^\d+\.\s+(click|type|scroll|assert|hover|drag|read|refresh):/gm;
const INTERACTION_VERBS = /^\d+\.\s+(click|type|drag):/gm;

interface TestStats {
    criticality: string;
    steps: number;
    interactions: number;
}

/** What the index summarizes about one test. Unreadable files count as empty. */
async function scanTest(outputDir: string, path: string): Promise<TestStats> {
    try {
        const content = await readFile(join(outputDir, path), "utf-8");
        return {
            criticality: content.match(/criticality:\s*(\w+)/)?.[1] ?? "",
            steps: content.match(STEP_VERBS)?.length ?? 0,
            interactions: content.match(INTERACTION_VERBS)?.length ?? 0,
        };
    } catch (err) {
        debugLog("Skipping a test file the index could not read", { path, err });
        return { criticality: "", steps: 0, interactions: 0 };
    }
}

/** What the run could not deliver, for the index to say out loud. */
export interface SuiteGaps {
    /** Tests the review cycle removed that nothing put back. */
    lost?: ReadonlySet<string>;
}

export async function generateIndex(outputDir: string, state: CoverageState, gaps: SuiteGaps = {}): Promise<void> {
    const testPaths = await listTestFiles(outputDir);
    const testsByFolder = new Map<string, string[]>();

    for (const p of testPaths) {
        const parts = p.split("/");
        if (parts.length >= 3) {
            const folder = parts[1]!;
            const existing = testsByFolder.get(folder) ?? [];
            existing.push(p);
            testsByFolder.set(folder, existing);
        }
    }

    const stats = state.summary();
    const folders = [...testsByFolder.entries()].map(([name, tests]) => ({
        name,
        test_count: tests.length,
    }));

    const critCounts = new Map<string, number>(CRITICALITY_LEVELS.map((level) => [level, 0]));
    let totalSteps = 0;
    let totalInteractions = 0;

    // One read per test, all at once: a suite is hundreds of small files and
    // nothing here depends on another file's contents, so reading them in
    // sequence spent the whole index write waiting on the disk.
    const scanned = await Promise.all(testPaths.map((path) => scanTest(outputDir, path)));
    for (const stats of scanned) {
        if (critCounts.has(stats.criticality)) {
            critCounts.set(stats.criticality, (critCounts.get(stats.criticality) ?? 0) + 1);
        }
        totalSteps += stats.steps;
        totalInteractions += stats.interactions;
    }

    const untested = untestedFeatures(state);
    const lost = [...(gaps.lost ?? [])].sort();
    const totalTests = testPaths.length;
    const avgSteps = totalTests > 0 ? (totalSteps / totalTests).toFixed(1) : "0";

    let content = `---
total_tests: ${totalTests}
total_folders: ${folders.length}
avg_steps_per_test: ${avgSteps}
total_interactions: ${totalInteractions}
features_without_tests: ${untested.length}
tests_lost_in_review: ${lost.length}
criticality:
${CRITICALITY_LEVELS.map((level) => `  ${level}: ${critCounts.get(level) ?? 0}`).join("\n")}
folders:
${folders.map((f) => `  - name: "${f.name}"\n    test_count: ${f.test_count}`).join("\n")}
---

# Test Suite Index

Generated by BFS exploration. ${stats.tested} nodes tested, ${stats.skipped} skipped.

## Folders

| Folder | Tests |
|--------|-------|
${folders.map((f) => `| ${f.name} | ${f.test_count} |`).join("\n")}

## All Tests

${[...testsByFolder.entries()].flatMap(([_folder, tests]) => tests.map((t) => `- \`${t}\``)).join("\n")}
${renderSection("Features with no tests", "the run walked these and produced no test for them - re-run the planner to cover them", untested)}${renderSection("Tests lost in review", "the review cycle removed these and nothing could put them back - re-run the planner to regenerate them", lost)}`;

    await writeFile(join(outputDir, TESTS_DIR, TEST_INDEX_FILE), content, "utf-8");
}

/**
 * Features the run walked but produced no test for - skipped outright, or
 * explored and moved on from. The suite's real coverage gap: unlike a count that
 * disagrees with disk, each of these names something a re-run can fix.
 */
function untestedFeatures(state: CoverageState): string[] {
    const withTests = new Set(state.testsWritten.keys());
    return [...state.nodes.values()]
        .filter((node) => node.status !== "tested" || !withTests.has(node.id))
        .map((node) => (node.routePath != null ? `${node.name} (${node.routePath})` : node.name))
        .sort();
}

/** A gap section, or nothing at all when there is no gap to report. */
function renderSection(title: string, explanation: string, items: readonly string[]): string {
    if (items.length === 0) return "";
    return `
## ${title}

${items.length} ${items.length === 1 ? "entry" : "entries"} - ${explanation}.

${items.map((item) => `- ${item}`).join("\n")}
`;
}
